import {
	Component,
	Inject,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	Validators,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	MAT_BOTTOM_SHEET_DATA,
	MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import {
	EmailSender,
	EmailSenderService,
} from '@lmc-app/core/services/email-sender.service';
import {
	DocumentOption,
	EmailTemplate,
	EmailTemplateService,
} from '@lmc-app/core/services/email-template.service';
import { Store } from '@ngrx/store';
import { selectUser, State } from '@lmc-app/store';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { User } from '@lmc-app/shared/interface/user.interface';
import { Applicant } from '@lmc-app/shared/enum/applicant.enum';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDialogTitle } from '@angular/material/dialog';
import {
	MatStepper,
	MatStep,
	MatStepLabel,
	MatStepperNext,
	MatStepperPrevious,
} from '@angular/material/stepper';
import { NgIf, NgFor } from '@angular/common';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelectionList, MatListOption } from '@angular/material/list';
import { MatCard, MatCardContent } from '@angular/material/card'; // Legacy type for backward compatibility

// Legacy type for backward compatibility
export interface EmailOption extends DocumentOption {}

export interface EmailFormData {
	templateId: string;
	selectedOptions: string[];
	recipient: string;
	subject: string;
	additionalNotes?: string;
}

@Component({
	selector: 'app-send-email-form',
	templateUrl: './send-email-form.component.html',
	styleUrls: ['./send-email-form.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		{
			provide: STEPPER_GLOBAL_OPTIONS,
			useValue: { showError: true },
		},
	],
	imports: [
		MatIconButton,
		MatIcon,
		MatDialogTitle,
		MatStepper,
		MatStep,
		ReactiveFormsModule,
		MatStepLabel,
		NgIf,
		MatFormField,
		MatLabel,
		MatSelect,
		MatOption,
		NgFor,
		MatInput,
		MatCheckbox,
		MatSelectionList,
		MatListOption,
		MatButton,
		MatStepperNext,
		MatCard,
		MatCardContent,
		MatStepperPrevious,
	],
})
export class SendEmailFormComponent implements OnInit, OnDestroy {
	application: any;
	emailFormGroup: FormGroup;
	emailSenders: EmailSender[] = [];
	emailTemplates: EmailTemplate[] = [];
	selectedTemplate: EmailTemplate | null = null;
	availableOptions: EmailOption[] = [];
	user: User = null;
	private destroy$ = new Subject<void>();

	protected readonly Applicant = Applicant;

	ngOnInit() {
		this.loadEmailTemplates();

		this.store
			.select(selectUser)
			.pipe(
				filter((user) => !!user),
				take(1),
				takeUntil(this.destroy$)
			)
			.subscribe((user) => {
				this.user = user;
				this.loadEmailSenders(user);
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	constructor(
		@Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
		private readonly apiService: LegacyApiService,
		private readonly emailSendersService: EmailSenderService,
		private readonly bottomSheetRef: MatBottomSheetRef<SendEmailFormComponent>,
		private readonly emailTemplateService: EmailTemplateService,
		private readonly formBuilder: FormBuilder,
		protected readonly sanitizer: DomSanitizer,
		private readonly snackBar: MatSnackBar,
		private readonly store: Store<State>
	) {
		if (this.data) {
			this.application = this.data.application;
		}
		this.emailFormGroup = this.formBuilder.group({
			templateId: [null, Validators.required],
			selectedOptions: [[], Validators.required],
			applicant: [Applicant.MAIN, Validators.required],
			senderEmail: [[], Validators.required],
			recipientName: [
				this.application.Contact_Name.Full_Name,
				Validators.required,
			],
			recipientEmail: [this.application.Email1, Validators.required],
			subject: [null, Validators.required],
			content: [null],
		});

		this.emailFormGroup
			.get('templateId')
			?.valueChanges.subscribe((templateId) => {
				this.onTemplateChange(templateId);
			});

		this.emailFormGroup.controls.applicant.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((applicant: Applicant) => {
				switch (applicant) {
					case Applicant.MAIN:
						this.emailFormGroup.controls.recipientName.patchValue(
							this.application.Contact_Name.Full_Name
						);
						this.emailFormGroup.controls.recipientEmail.patchValue(
							this.application.Email1
						);
						break;
					case Applicant.CO:
						this.emailFormGroup.controls.recipientName.patchValue(
							this.application.Co_Applicant_Name
						);
						this.emailFormGroup.controls.recipientEmail.patchValue(
							this.application.Email
						);
						break;
				}
			});

		this.emailFormGroup.get('selectedOptions')?.valueChanges.subscribe(() => {
			this.updatePreview();
		});
	}

	onTemplateChange(templateId: string): void {
		this.selectedTemplate =
			this.emailTemplates.find((t) => t.id === templateId) || null;

		if (this.selectedTemplate) {
			// Extract document options from template's options property
			// Check if template has DOCUMENT_LIST options (dynamic check based on template data)
			if (
				this.selectedTemplate.options?.DOCUMENT_LIST &&
				Array.isArray(this.selectedTemplate.options.DOCUMENT_LIST) &&
				this.selectedTemplate.options.DOCUMENT_LIST.length > 0
			) {
				// Map template options to EmailOption format
				this.availableOptions = this.selectedTemplate.options.DOCUMENT_LIST.map(
					(option) => ({
						id: option.key,
						label: option.name,
						value: option.value,
						description: option.description,
					})
				);
			} else {
				this.availableOptions = [];
			}

			// Update validators based on whether options are available
			const selectedOptionsControl = this.emailFormGroup.get('selectedOptions');
			if (this.availableOptions.length > 0) {
				selectedOptionsControl?.setValidators([Validators.required]);
			} else {
				selectedOptionsControl?.clearValidators();
			}
			selectedOptionsControl?.updateValueAndValidity();

			// Use template subject from backend
			this.emailFormGroup.patchValue({
				subject: this.selectedTemplate.subject,
				selectedOptions: [], // Reset selected options
			});
		} else {
			this.availableOptions = [];
		}

		this.updatePreview();
	}

	onPreview(): void {
		this.updatePreview();
	}

	updatePreview(): void {
		if (!this.selectedTemplate) {
			this.emailFormGroup.controls.content.patchValue(null);
			return;
		}

		const formData = this.emailFormGroup.value;
		const templateId = this.selectedTemplate.id;

		const previewData = {
			data: {
				customerName: this.emailFormGroup.controls.recipientName.value,
				selectedDocuments: formData.selectedOptions || [],
				applicationNumber: this.application.id,
				deadlineDays: 7,
			},
		};

		this.emailTemplateService
			.previewEmailTemplate(templateId, previewData)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					this.emailFormGroup.controls.content.patchValue(response.content);
				},
				error: (error) => {
					console.error(error);
					this.snackBar.open(
						error.message,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
				},
			});
	}

	onCancel(): void {
		this.bottomSheetRef.dismiss();
	}

	isAllSelected(): boolean {
		const selectedOptions =
			this.emailFormGroup.get('selectedOptions')?.value || [];
		return (
			this.availableOptions.length > 0 &&
			selectedOptions.length === this.availableOptions.length
		);
	}

	isIndeterminate(): boolean {
		const selectedOptions =
			this.emailFormGroup.get('selectedOptions')?.value || [];
		return (
			selectedOptions.length > 0 &&
			selectedOptions.length < this.availableOptions.length
		);
	}

	toggleSelectAll(): void {
		const selectedOptions =
			this.emailFormGroup.get('selectedOptions')?.value || [];

		if (this.isAllSelected()) {
			// Deselect all
			this.emailFormGroup.patchValue({ selectedOptions: [] });
		} else {
			// Select all
			const allOptionIds = this.availableOptions.map((option) => option.id);
			this.emailFormGroup.patchValue({ selectedOptions: allOptionIds });
		}

		this.updatePreview();
	}

	onSend(): void {
		const {
			applicant,
			senderEmail,
			recipientName,
			recipientEmail,
			subject,
			content,
		} = this.emailFormGroup.value;

		const senderName = this.emailSenders.find(
			(sender) => sender.email === senderEmail
		).name;

		const request = {
			applicant: applicant,
			senderName: senderName,
			senderEmail: senderEmail,
			recipientName: recipientName,
			recipientEmail: recipientEmail,
			subject: subject,
			content: content,
			userId: this.user?.employeeID,
		};

		this.apiService
			.sendEmailToApplicantByApplicationId(this.application.id, request)
			.subscribe({
				next: (res) => {
					this.bottomSheetRef.dismiss(res);
				},
				error: (error) => {
					this.snackBar.open(
						error.message,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
					console.error(error);
				},
			});
	}

	private loadEmailSenders(user: User): void {
		// Load senders from backend API
		this.emailSendersService
			.getEmailSenders()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					this.emailSenders = [
						...response,
						{ name: user.full_name, email: user.email },
					];
					this.emailFormGroup.controls.senderEmail.setValue(
						this.emailSenders[0].email
					);
				},
				error: (error) => {
					console.error(error);
					this.snackBar.open(
						error.message,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
					this.emailSenders = [];
				},
			});
	}

	private loadEmailTemplates(): void {
		// Load visible templates from backend API
		this.emailTemplateService
			.getEmailTemplates({ visible: true })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					this.emailTemplates = response;
				},
				error: (error) => {
					console.error(error);
					this.snackBar.open(
						error.message,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
					this.emailTemplates = [];
				},
			});
	}
}
